import { useState, useEffect, useCallback, useRef } from 'react'

// Game ID to folder slug mapping
const GAME_SLUG_MAP = {
  '1029384756': 'tic-tac-toe',
  '2938475610': 'snake',
  '4756382910': 'memory-match',
  '5647382910': 'breakout',
  '6758493021': 'pong',
  '8392746501': 'car-racing',
  '7583921064': 'car-dodger',
  '5739281046': 'neon-drift',
  '6284193750': 'sky-stack',
  '7391824650': 'jump-box'
}

export function getInstallHelpText() {
  if (typeof navigator === 'undefined') {
    return {
      title: 'Install',
      message: 'Installation is only available in supported browsers.'
    }
  }

  const ua = navigator.userAgent || ''
  const isIOS = /iPad|iPhone|iPod/i.test(ua)
  const isAndroid = /Android/i.test(ua)
  const isIOSChrome = /CriOS/i.test(ua)
  const isIOSFirefox = /FxiOS/i.test(ua)
  const isIOSEdge = /EdgiOS/i.test(ua)
  const isIOSSafari = isIOS && /Safari/i.test(ua) && !isIOSChrome && !isIOSFirefox && !isIOSEdge
  const isEdgeDesktop = /Edg\//i.test(ua)
  const isChromeDesktop = /Chrome\//i.test(ua) && !isEdgeDesktop
  const isFirefox = /Firefox\//i.test(ua)

  if (isIOSSafari) {
    return {
      title: 'Add to Home Screen',
      message:
        'To install this game on iPhone/iPad:\n\n' +
        '1) Tap the Share button\n' +
        '2) Tap “Add to Home Screen”\n' +
        '3) Confirm Add\n\n' +
        'Note: iOS installs PWAs through Safari (not Chrome-style install prompts).'
    }
  }

  if (isIOS && !isIOSSafari) {
    return {
      title: 'Install on iOS',
      message:
        'On iPhone/iPad, installation works via Safari:\n\n' +
        '1) Open this page in Safari\n' +
        '2) Share → Add to Home Screen'
    }
  }

  if (isAndroid) {
    return {
      title: 'Install app',
      message:
        'To install on Android:\n\n' +
        '• Use your browser menu → “Install app” / “Add to Home screen”\n' +
        '• If you see an install icon in the address bar, use that'
    }
  }

  if (isChromeDesktop || isEdgeDesktop) {
    return {
      title: 'Install app',
      message:
        'To install on desktop:\n\n' +
        '• Click the install icon in the address bar, or\n' +
        '• Browser menu → Install app'
    }
  }

  if (isFirefox) {
    return {
      title: 'Not supported',
      message:
        'Firefox typically does not support installing PWAs as standalone apps.\n\n' +
        'You can still play in the browser, or use Chrome/Edge/Safari for installation.'
    }
  }

  return {
    title: 'Install',
    message:
      'If your browser supports app install, use its menu to “Install app” / “Add to Home Screen”.\n\n' +
      'If you don’t see that option, this browser/OS may not support PWA installation.'
  }
}

/**
 * Per-Game PWA Installation Hook
 * Each game is installable as its own separate PWA app.
 * The main website is NOT installable - only individual games.
 */
export function useGamePWA(gameId) {
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [isInstallable, setIsInstallable] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [isRegistering, setIsRegistering] = useState(false)
  const [error, setError] = useState(null)
  const registrationRef = useRef(null)
  const manifestLinkRef = useRef(null)

  const gameSlug = GAME_SLUG_MAP[gameId]

  // Setup game-specific PWA on mount
  useEffect(() => {
    if (!gameId || !gameSlug) return

    let cleanup = () => {}

    const setupGamePWA = async () => {
      setIsRegistering(true)
      setError(null)

      try {
        // Step 1: Link game-specific manifest
        const existingManifest = document.querySelector('link[rel="manifest"]')
        if (existingManifest) {
          existingManifest.remove()
        }

        const manifestLink = document.createElement('link')
        manifestLink.rel = 'manifest'
        manifestLink.href = `/games/${gameSlug}/manifest.json`
        document.head.appendChild(manifestLink)
        manifestLinkRef.current = manifestLink

        // Step 2: Register game-specific service worker with scope
        // Service workers are at root level so they can scope to /play/{gameId}
        if ('serviceWorker' in navigator) {
          const swPath = `/sw-${gameSlug}.js`
          const scope = `/play/${gameId}`
          
          const registration = await navigator.serviceWorker.register(swPath, { scope })
          registrationRef.current = registration
          console.log(`[PWA] Registered SW for ${gameSlug} with scope: ${scope}`)

          // The install prompt usually only appears once the page is controlled by the SW.
          // If we're already under the SW scope but not yet controlled, reload once.
          try {
            const reloadKey = `pwa:sw-controlled:${gameId}`
            const isUnderScope = window.location.pathname.startsWith(scope)
            const alreadyReloaded = sessionStorage.getItem(reloadKey) === '1'
            const hasController = Boolean(navigator.serviceWorker.controller)

            if (isUnderScope && !hasController && !alreadyReloaded) {
              sessionStorage.setItem(reloadKey, '1')
              window.location.reload()
              return
            }
          } catch {
            // ignore
          }
        }

        // Step 3: Check if already installed (standalone mode)
        const isStandaloneMode = 
          window.matchMedia('(display-mode: standalone)').matches ||
          window.navigator.standalone === true

        if (isStandaloneMode) {
          setIsInstalled(true)
        }

        setIsRegistering(false)
      } catch (err) {
        console.error('[PWA] Setup failed:', err)
        setError(err.message)
        setIsRegistering(false)
      }
    }

    setupGamePWA()

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault()
      setDeferredPrompt(e)
      setIsInstallable(true)
      console.log('[PWA] Install prompt captured for game:', gameSlug)
    }

    const handleAppInstalled = () => {
      setDeferredPrompt(null)
      setIsInstallable(false)
      setIsInstalled(true)
      console.log('[PWA] App installed:', gameSlug)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    cleanup = () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
      
      // Clean up manifest link
      if (manifestLinkRef.current) {
        manifestLinkRef.current.remove()
        manifestLinkRef.current = null
      }
    }

    return cleanup
  }, [gameId, gameSlug])

  // Trigger install prompt
  const installGame = useCallback(async () => {
    if (!deferredPrompt) {
      console.log('[PWA] No install prompt available')
      return { success: false, reason: 'no-prompt' }
    }

    try {
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      
      if (outcome === 'accepted') {
        setDeferredPrompt(null)
        setIsInstallable(false)
        setIsInstalled(true)
        return { success: true }
      }
      
      return { success: false, reason: 'dismissed' }
    } catch (err) {
      console.error('[PWA] Install failed:', err)
      return { success: false, reason: err.message }
    }
  }, [deferredPrompt])

  return {
    isInstallable,
    isInstalled,
    isRegistering,
    error,
    installGame,
    gameSlug
  }
}

// Legacy export for backwards compatibility - used to detect standalone mode
export function usePWAInstall() {
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  )
  
  return {
    isInstallable: false,
    isInstalled: isStandalone,
    isStandalone,
    installApp: () => Promise.resolve(false)
  }
}

/**
 * Apply game-specific manifest for PWA installation.
 * Call this on game detail pages to enable the install prompt.
 * Returns a cleanup function to remove the manifest.
 */
export function applyGameManifest(game) {
  if (!game || !game.id) return () => {}
  
  const gameSlug = GAME_SLUG_MAP[game.id]
  if (!gameSlug) return () => {}

  // Remove any existing manifest
  const existingManifest = document.querySelector('link[rel="manifest"]')
  if (existingManifest) {
    existingManifest.remove()
  }

  // Add game-specific manifest
  const manifestLink = document.createElement('link')
  manifestLink.rel = 'manifest'
  manifestLink.href = `/games/${gameSlug}/manifest.json`
  document.head.appendChild(manifestLink)

  // Return cleanup function
  return () => {
    if (manifestLink.parentNode) {
      manifestLink.remove()
    }
  }
}
